import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { TopNav } from '../ui/top-nav';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { lambdaService, UploadResponse } from '../../services/lambdaService';
import {
  Upload,
  File,
  X,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  CloudUpload,
  Folder,
  Link as LinkIcon,
  Download,
  Globe,
  Wifi,
  Database,
  Cloud,
  FileText,
  AlertTriangle,
  Info,
  Music,
  Video,
  Image,
  FileSpreadsheet,
  FileType2,
  Table2
} from 'lucide-react';
import { S3CredentialsForm, CloudProvider } from './S3CredentialsForm';
import { S3FileBrowser } from './S3FileBrowser';
import { S3CredentialsStatus } from '../../types/s3Transfer';
import { SQLIngestionTab } from './SQLIngestionTab';
import { dataSourceService } from '../../services/dataSourceService';
import { DataSource } from '../../types/dataSource';
import { DataSourceForm } from '../data-sources/DataSourceForm';
import { useUploadDemoContext } from './UploadDemoContext';

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  message?: string;
  warnings?: string[];
  errors?: string[];
  s3Key?: string;
}

export const UploadPage: React.FC = () => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isMediaDragOver, setIsMediaDragOver] = useState(false);
  const [isImageDragOver, setIsImageDragOver] = useState(false);
  const [isPdfDragOver, setIsPdfDragOver] = useState(false);
  const [pdfExtractTables, setPdfExtractTables] = useState(true);
  const [extractMode, setExtractMode] = useState<'document' | 'table' | 'figure' | 'describe' | 'all'>('document');
  const [pendingImageFiles, setPendingImageFiles] = useState<File[]>([]);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{
    download_id: string;
    filename: string;
    progress: number;
    status: string;
    speed_mbps?: number;
    eta_seconds?: number;
    // Processing phase fields
    processingProgress?: number;
    currentChunks?: number;
    expectedChunks?: number;
    totalRows?: number;
  } | null>(null);
  const processingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activeTab, setActiveTab] = useState<string>('direct');
  const [cloudProvider, setCloudProvider] = useState<CloudProvider>('aws');
  const [hasS3Credentials, setHasS3Credentials] = useState(false);
  const [hasGcpCredentials, setHasGcpCredentials] = useState(false);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [showAddSource, setShowAddSource] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [migrating, setMigrating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const targetFolder = (location.state as any)?.targetFolder || '';

  // Demo context — only non-null on demo.bigsplicer.com
  const demoCtx = useUploadDemoContext();

  // Derive effective state: demo overrides when active
  const effectiveTab = demoCtx?.activeTab ?? activeTab;
  const effectiveDragData = demoCtx?.dragOverCard === 'data' || isDragOver;
  const effectiveDragPdf = demoCtx?.dragOverCard === 'pdf' || isPdfDragOver;
  const effectiveDragImage = demoCtx?.dragOverCard === 'image' || isImageDragOver;
  const effectiveDragMedia = demoCtx?.dragOverCard === 'media' || isMediaDragOver;

  // Build display queue: demo files take priority when active
  const displayQueue: Array<{ name: string; size: number; progress: number; status: string; message?: string; warnings?: string[]; errors?: string[] }> = demoCtx?.uploadingFiles
    ? demoCtx.uploadingFiles.map((f) => ({ name: f.fileName, size: f.fileSize, progress: f.progress, status: f.status, message: f.message }))
    : uploadingFiles.map((f) => ({ name: f.file.name, size: f.file.size, progress: f.progress, status: f.status, message: f.message, warnings: f.warnings, errors: f.errors }));

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const checkExcelProcessingStatus = async (s3Key: string, fileName: string, maxAttempts = 600): Promise<boolean> => {
    // Extract the base filename without extension and timestamp prefix
    const fileNameWithoutExt = fileName.replace(/\.(xlsx|xls|pdf|m4a|mp3|wav|ogg|flac|wma|aac|webm|mp4|mkv|avi|mov|wmv|jpg|jpeg|png|tiff|tif|bmp|webp)$/i, '');
    const baseFileName = fileNameWithoutExt.replace(/^\d{8}_\d{6}-/, '');

    // Expected parquet path: users/{user_id}/data/{filename}/Sheet1.parquet
    const userIdMatch = s3Key.match(/users\/([^\/]+)\//);
    if (!userIdMatch) return false;

    const userId = userIdMatch[1];
    const expectedDataKey = `users/${userId}/data/${fileNameWithoutExt}/Sheet1.parquet`;

    // Poll for up to 20 minutes (600 attempts * 2 seconds)
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Check if the parquet file exists by calling the list files Lambda
        const response = await lambdaService.listFiles();
        if (response.success && response.files) {
          // Check if any file matches the expected parquet file (s3Key is the full path)
          const processed = response.files.some((file: any) =>
            (file.s3Key && file.s3Key.includes(fileNameWithoutExt)) ||
            (file.fileName && file.fileName.includes(fileNameWithoutExt))
          );

          if (processed) {
            return true;
          }
        }
      } catch (error) {
        console.warn(`Polling attempt ${attempt + 1} failed:`, error);
      }

      // Wait 2 seconds before next attempt
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    return false;
  };


  const handleFiles = async (files: FileList) => {
    const fileArray = Array.from(files);
    const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB in bytes

    // Check for files exceeding 5GB limit
    const oversizedFiles = fileArray.filter(file => file.size > MAX_FILE_SIZE);
    const validFiles = fileArray.filter(file => file.size <= MAX_FILE_SIZE);

    // Show error for oversized files
    if (oversizedFiles.length > 0) {
      oversizedFiles.forEach(file => {
        const sizeMB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
        toast.error(`${file.name} exceeds 5GB limit (${sizeMB}GB). Please split or compress the file.`, {
          duration: 8000
        });
      });
    }

    // Only process valid files
    if (validFiles.length === 0) {
      return;
    }

    // Add valid files to uploading list
    const newUploadingFiles = validFiles.map(file => ({
      file,
      progress: 0,
      status: 'uploading' as const
    }));

    setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

    // Upload each valid file
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      await uploadFile(file, i + uploadingFiles.length);
    }
  };

  const uploadFile = async (file: File, index: number) => {
    try {
      // For image files, add extraction mode marker to filename
      let fileToUpload = file;
      if (/\.(jpg|jpeg|png|tiff|tif|bmp|webp)$/i.test(file.name) && extractMode !== 'document') {
        const ext = file.name.match(/\.[^.]+$/)?.[0] || '';
        const stem = file.name.replace(/\.[^.]+$/, '');
        const modeTag = extractMode === 'table' ? '__TABLEMODE'
          : extractMode === 'figure' ? '__FIGUREPARSE'
          : extractMode === 'describe' ? '__DESCRIBE'
          : extractMode === 'all' ? '__ALLMODE' : '';
        if (modeTag) {
          fileToUpload = new globalThis.File([file], `${stem}${modeTag}${ext}`, { type: file.type });
        }
      }

      const result = await lambdaService.uploadFile(fileToUpload, (progress) => {
        setUploadingFiles(prev => prev.map((uf, i) =>
          i === index ? { ...uf, progress } : uf
        ));
      });

      if (result.success) {
        // Create upload completion message
        let message = result.message || 'Upload completed successfully';
        let finalS3Key = result.s3Key;

        // If targetFolder is specified, move the file to that folder
        if (targetFolder && result.s3Key) {
          try {
            // console.log(`Moving uploaded file to folder: ${targetFolder}`);
            const moveResult = await lambdaService.moveFile(result.s3Key, targetFolder);

            if (moveResult.success) {
              message = `Uploaded and moved to ${targetFolder}`;
              // Update the s3Key to the new location
              const fileName = result.s3Key.split('/').pop() || file.name;
              finalS3Key = `users/{user_id}/data/${targetFolder}/${fileName}`;
              // console.log(`File successfully moved to: ${finalS3Key}`);
            } else {
              message = `Uploaded but failed to move to ${targetFolder}: ${moveResult.error}`;
              toast.warning(`${file.name} uploaded but could not be moved to folder`);
            }
          } catch (moveError) {
            // console.error('Error moving file to folder:', moveError);
            message = `Uploaded but failed to move to ${targetFolder}`;
            toast.warning(`${file.name} uploaded but could not be moved to folder`);
          }
        }

        // Check if this is a file that needs server-side processing
        const isExcelFile = /\.(xlsx|xls)$/i.test(file.name);
        const isPdfFile = /\.pdf$/i.test(file.name);
        const isAudioFile = /\.(m4a|mp3|wav|ogg|flac|wma|aac|webm)$/i.test(file.name);
        const isVideoFile = /\.(mp4|mkv|avi|mov|wmv)$/i.test(file.name);
        const isImageFile = /\.(jpg|jpeg|png|tiff|tif|bmp|webp)$/i.test(file.name);
        const needsProcessing = isExcelFile || isPdfFile || isAudioFile || isVideoFile || isImageFile;

        if (needsProcessing && result.s3Key) {
          const fileType = isImageFile ? 'Image' : isVideoFile ? 'Video' : isAudioFile ? 'Audio' : isPdfFile ? 'PDF' : 'Excel';
          const processingMsg = isImageFile
            ? 'Extracting text from image...'
            : isVideoFile
            ? 'Transcribing video...'
            : isAudioFile
            ? 'Transcribing audio...'
            : isPdfFile
            ? 'Extracting text from PDF...'
            : 'Processing Excel file...';
          const toastMsg = isImageFile
            ? `${file.name} uploaded - extracting text via OCR...`
            : isVideoFile
            ? `${file.name} uploaded - transcribing video...`
            : isAudioFile
            ? `${file.name} uploaded - transcribing audio...`
            : isPdfFile
            ? `${file.name} uploaded - extracting text...`
            : `${file.name} uploaded - processing worksheets...`;

          // Set status to processing and start polling
          setUploadingFiles(prev => prev.map((uf, i) =>
            i === index ? {
              ...uf,
              progress: 100,
              status: 'processing',
              message: processingMsg,
              s3Key: finalS3Key
            } : uf
          ));

          toast.info(toastMsg);

          // Poll for processing completion (up to 20 min for GPU cold starts)
          const processed = await checkExcelProcessingStatus(result.s3Key, file.name);

          if (processed) {
            setUploadingFiles(prev => prev.map((uf, i) =>
              i === index ? {
                ...uf,
                status: 'completed',
                message: `${fileType} file processed successfully`,
                s3Key: finalS3Key
              } : uf
            ));
            toast.success(`${file.name} processed successfully`);
          } else {
            setUploadingFiles(prev => prev.map((uf, i) =>
              i === index ? {
                ...uf,
                status: 'error',
                message: 'Processing timed out - file may still be processing in background'
              } : uf
            ));
            toast.warning(`${file.name} processing timed out - check files page`);
          }
        } else {
          // Non-Excel file - mark as completed immediately
          setUploadingFiles(prev => prev.map((uf, i) =>
            i === index ? {
              ...uf,
              progress: 100,
              status: 'completed',
              message: message,
              warnings: result.warnings,
              errors: result.errors,
              s3Key: finalS3Key
            } : uf
          ));

          // Show appropriate toast based on upload results
          if (result.errors && result.errors.length > 0) {
            toast.error(`${file.name} uploaded with errors - check details`);
          } else if (result.warnings && result.warnings.length > 0) {
            toast.warning(`${file.name} uploaded with warnings - check details`);
          } else {
            if (targetFolder) {
              toast.success(`${file.name} uploaded to ${targetFolder}`);
            } else {
              toast.success(`${file.name} uploaded successfully`);
            }
          }
        }
      } else {
        // Enhanced error handling
        let errorMessage = result.error || 'Upload failed';

        // Handle specific error types
        if (errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch')) {
          errorMessage = 'Connection error - check network and try again';
        } else if (errorMessage.includes('400')) {
          errorMessage = 'Invalid file format or missing parameters';
        } else if (errorMessage.includes('502')) {
          errorMessage = 'Service temporarily unavailable - try again later';
        } else if (errorMessage.includes('403') || errorMessage.includes('Authentication required')) {
          errorMessage = 'Session expired - please refresh the page and log in again';
        }

        setUploadingFiles(prev => prev.map((uf, i) =>
          i === index ? {
            ...uf,
            status: 'error',
            message: errorMessage
          } : uf
        ));
        toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
      }
    } catch (error) {
      setUploadingFiles(prev => prev.map((uf, i) =>
        i === index ? {
          ...uf,
          status: 'error',
          message: `Upload error: ${error}`
        } : uf
      ));
      toast.error(`Error uploading ${file.name}`);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handleMediaDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsMediaDragOver(true);
  }, []);

  const handleMediaDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsMediaDragOver(false);
  }, []);

  const handleMediaDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsMediaDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handlePdfFiles = async (files: FileList) => {
    // If extract tables is ON, tag the filenames so the bridge knows
    if (!pdfExtractTables) {
      // Tag PDFs with __NOTABLES so bridge skips table extraction
      const dt = new DataTransfer();
      Array.from(files).forEach(f => {
        if (/\.pdf$/i.test(f.name)) {
          const stem = f.name.replace(/\.pdf$/i, '');
          const tagged = new globalThis.File([f], `${stem}__NOTABLES.pdf`, { type: f.type });
          dt.items.add(tagged);
        } else {
          dt.items.add(f);
        }
      });
      handleFiles(dt.files);
    } else {
      handleFiles(files);
    }
  };

  const handlePdfDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsPdfDragOver(true);
  }, []);

  const handlePdfDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsPdfDragOver(false);
  }, []);

  const handlePdfDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsPdfDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handlePdfFiles(files);
    }
  }, [pdfExtractTables]);

  const handlePdfInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handlePdfFiles(files);
    }
  };

  const handleImageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsImageDragOver(true);
  }, []);

  const handleImageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsImageDragOver(false);
  }, []);

  const handleImageDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsImageDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const fileArray = Array.from(files);
      const imageFiles = fileArray.filter(f => /\.(jpg|jpeg|png|tiff|tif|bmp|webp)$/i.test(f.name));
      if (imageFiles.length > 0) {
        setPendingImageFiles(imageFiles);
        setExtractMode('document');
        setShowImageOptions(true);
      } else {
        handleFiles(files);
      }
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Check if these are image files — if so, show options modal first
      const fileArray = Array.from(files);
      const allImages = fileArray.every(f => /\.(jpg|jpeg|png|tiff|tif|bmp|webp)$/i.test(f.name));
      if (allImages && fileArray.length > 0) {
        setPendingImageFiles(fileArray);
        setExtractMode('document');
        setShowImageOptions(true);
        // Reset the input so the same files can be re-selected
        e.target.value = '';
        return;
      }
      handleFiles(files);
    }
  };

  const handleImageUploadConfirm = () => {
    if (pendingImageFiles.length === 0) return;
    setShowImageOptions(false);
    const dt = new DataTransfer();
    pendingImageFiles.forEach(f => dt.items.add(f));
    handleFiles(dt.files);
    setPendingImageFiles([]);
  };

  const handleImageUploadCancel = () => {
    setShowImageOptions(false);
    setPendingImageFiles([]);
  };

  const removeUploadingFile = (index: number) => {
    setUploadingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearCompleted = () => {
    setUploadingFiles(prev => prev.filter(uf => uf.status === 'uploading'));
  };

  // setTimeout-based polling — no request stacking, with backoff.
  // Each poll waits for the previous request to finish before scheduling the next.
  const startProcessingPolling = useCallback((filename: string, downloadId: string) => {
    if (processingPollRef.current) clearTimeout(processingPollRef.current);

    setDownloadProgress(prev => prev ? {
      ...prev,
      status: 'processing',
      progress: 100,
      speed_mbps: undefined,
      eta_seconds: undefined,
      processingProgress: 0,
    } : null);

    let notFoundCount = 0;
    let interval = 15000; // start at 15s, back off to 30s max

    const poll = async () => {
      if (document.hidden) {
        // Tab not visible — skip this tick, retry later
        processingPollRef.current = setTimeout(poll, interval);
        return;
      }
      try {
        const resp = await lambdaService.getProcessingStatus();
        if (!resp.success) {
          processingPollRef.current = setTimeout(poll, interval);
          return;
        }

        const baseName = filename.replace(/^\d+-/, '').replace(/\.\w+$/, '').toLowerCase();
        const match = resp.processing_files.find(f =>
          f.original_filename?.toLowerCase().includes(baseName) ||
          f.filename?.toLowerCase().includes(baseName)
        );

        if (match) {
          notFoundCount = 0;
          interval = 15000; // reset backoff while actively processing
          const pct = match.chunk_info?.progress_percent ?? 0;
          setDownloadProgress(prev => prev ? {
            ...prev,
            status: 'processing',
            processingProgress: pct,
            currentChunks: match.chunk_info?.current_chunks,
            expectedChunks: match.chunk_info?.expected_chunks,
            totalRows: match.chunk_info?.total_rows,
          } : null);

          if (match.status === 'failed') {
            processingPollRef.current = null;
            setDownloadProgress(prev => prev ? { ...prev, status: 'failed' } : null);
            setIsDownloading(false);
            toast.error(`Processing failed: ${match.error_message || 'Unknown error'}`);
            return; // stop polling
          }
        } else {
          notFoundCount++;
          interval = Math.min(interval + 5000, 30000); // back off
          if (notFoundCount >= 3) {
            processingPollRef.current = null;
            setDownloadProgress(prev => prev ? { ...prev, status: 'ready', processingProgress: 100 } : null);
            setIsDownloading(false);
            toast.success(`${filename} is ready!`);
            return; // stop polling
          }
        }
      } catch {
        interval = Math.min(interval + 5000, 30000); // back off on error
      }
      processingPollRef.current = setTimeout(poll, interval);
    };

    processingPollRef.current = setTimeout(poll, 10000); // initial delay
  }, []);

  useEffect(() => {
    return () => {
      if (processingPollRef.current) clearTimeout(processingPollRef.current);
      if (downloadPollRef.current) clearTimeout(downloadPollRef.current);
    };
  }, []);

  const downloadPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startProgressPolling = (download_id: string) => {
    let interval = 8000; // start at 8s, back off to 20s

    const poll = async () => {
      if (document.hidden) {
        downloadPollRef.current = setTimeout(poll, interval);
        return;
      }
      try {
        const progress = await lambdaService.getDownloadProgress(download_id);

        if (!progress.success || progress.status === 'not_found') {
          downloadPollRef.current = null;
          setDownloadProgress(null);
          setIsDownloading(false);
          return;
        }

        setDownloadProgress({
          download_id,
          filename: progress.filename || 'Downloading...',
          progress: progress.progress || 0,
          status: progress.status || 'downloading',
          speed_mbps: progress.speed_mbps,
          eta_seconds: progress.eta_seconds
        });

        if (progress.status === 'completed') {
          downloadPollRef.current = null;
          const fname = progress.filename || 'file';
          toast.success(`Download complete — now processing ${fname}`);
          startProcessingPolling(fname, download_id);
          return;
        } else if (progress.status === 'failed') {
          downloadPollRef.current = null;
          setDownloadProgress(null);
          setIsDownloading(false);
          toast.error(`Download failed: ${progress.error || 'Unknown error'}`);
          return;
        }

        // Back off gradually
        interval = Math.min(interval + 2000, 20000);
      } catch {
        interval = Math.min(interval + 5000, 20000);
      }
      downloadPollRef.current = setTimeout(poll, interval);
    };

    downloadPollRef.current = setTimeout(poll, 5000); // first poll after 5s
  };

  const handleUrlDownload = async () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    // Validate URL format
    try {
      new URL(urlInput);
    } catch (e) {
      toast.error('Please enter a valid URL');
      return;
    }

    setIsDownloading(true);

    try {
      const result = await lambdaService.downloadFromUrl(urlInput);

      if (result.success) {
        // Clear input on success
        setUrlInput('');

        if (result.download_id) {
          // Backend supports progress tracking - start polling
          startProgressPolling(result.download_id);
          // Success toast will be shown when progress updates arrive
        } else {
          toast.success('Download started! File will appear in your Files page when complete.');
          setIsDownloading(false);
        }
      } else {
        toast.error(result.error || 'Failed to start download from URL');
        setIsDownloading(false);
      }
    } catch (error) {
      // console.error('URL download error:', error);
      toast.error('Network error - unable to start download. Please check your connection and try again.');
      setIsDownloading(false);
    }
  };

  const hasCompletedFiles = uploadingFiles.some(uf => uf.status === 'completed');
  const hasUploadingFiles = uploadingFiles.some(uf => uf.status === 'uploading');

  // Data source registry
  const loadDataSources = useCallback(async () => {
    try {
      const sources = await dataSourceService.listSources();
      setDataSources(sources);
    } catch {
      // ignore - service may not be deployed yet
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === 'cloud' || activeTab === 'sql') {
      loadDataSources();
    }
  }, [activeTab, loadDataSources]);

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await dataSourceService.migrate();
      toast.success(`Migrated ${res.migrated} data source${res.migrated !== 1 ? 's' : ''}`);
      loadDataSources();
    } catch (e: any) {
      toast.error(e.message || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    try {
      await dataSourceService.deleteSource(sourceId);
      toast.success('Data source removed');
      setDataSources((prev) => prev.filter((s) => s.source_id !== sourceId));
      if (selectedSourceId === sourceId) setSelectedSourceId(null);
    } catch (e: any) {
      toast.error(e.message || 'Delete failed');
    }
  };

  // Cloud Storage handlers
  const handleS3CredentialsSaved = (status: S3CredentialsStatus, showToast: boolean = false) => {
    if (status.success && status.has_credentials) {
      setHasS3Credentials(true);
      if (showToast) {
        toast.success('AWS S3 credentials saved successfully!');
      }
    } else {
      setHasS3Credentials(false);
    }
  };

  const handleGcpCredentialsSaved = (status: S3CredentialsStatus, showToast: boolean = false) => {
    if (status.success && status.has_credentials) {
      setHasGcpCredentials(true);
      if (showToast) {
        toast.success('GCP credentials saved successfully!');
      }
    } else {
      setHasGcpCredentials(false);
    }
  };

  const handleCloudTransferComplete = () => {
    toast.success('Files transferred successfully! They will appear in your dashboard shortly.');
  };

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="overflow-auto" style={{ height: 'calc(100vh - 4.75rem)' }}>
        <div className="p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CloudUpload className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-base font-semibold text-foreground">Upload Files</h1>
              <p className="text-sm text-muted-foreground">
                Import data from your computer, URLs, or S3 buckets
              </p>
            </div>
          </div>
          {targetFolder && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full border border-border text-sm">
              <Folder className="h-3.5 w-3.5 text-blue-500" />
              <span>Uploading to: <span className="font-semibold">{targetFolder}</span></span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Content - Left Column (full width for SQL tab) */}
          <div className={`${effectiveTab === 'sql' ? 'lg:col-span-3' : 'lg:col-span-2'} space-y-6`}>
            <Tabs value={effectiveTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6" data-demo="upload-tabs">
                <TabsTrigger value="direct" className="flex items-center gap-2">
                  <CloudUpload className="h-4 w-4" />
                  Direct Upload
                </TabsTrigger>
                <TabsTrigger value="url" className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  URL Download
                </TabsTrigger>
                <TabsTrigger value="cloud" className="flex items-center gap-2" data-demo="tab-cloud">
                  <Cloud className="h-4 w-4" />
                  Cloud Storage
                </TabsTrigger>
                <TabsTrigger value="sql" className="flex items-center gap-2" data-demo="tab-sql">
                  <Database className="h-4 w-4" />
                  SQL Ingestion
                </TabsTrigger>
              </TabsList>

              {/* Direct Upload Tab */}
              <TabsContent value="direct" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Data Files (top-left) */}
                  <Card className="border-2 border-dashed border-border hover:border-primary/50 transition-colors" data-demo="card-data">
                    <CardContent className="p-5">
                      <div
                        className={`flex flex-col items-center justify-center text-center space-y-2 transition-all ${effectiveDragData ? 'scale-[1.02]' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className={`p-2.5 rounded-full ${effectiveDragData ? 'bg-primary/20' : 'bg-muted'}`}>
                          <FileSpreadsheet className={`h-6 w-6 ${effectiveDragData ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">
                            {effectiveDragData ? 'Drop files now' : 'Data Files'}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            CSV, Parquet, Excel, JSON, TSV, logs
                          </p>
                        </div>
                        <Button size="sm" onClick={() => fileInputRef.current?.click()} className="mt-1">
                          <Upload className="h-3.5 w-3.5 mr-1.5" />
                          Choose Files
                        </Button>
                        <p className="text-[11px] text-muted-foreground">Max 5GB per file</p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileInput}
                          accept=".csv,.json,.parquet,.xlsx,.xls,.tsv,.txt,.log,.gz,.zip"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* PDF Documents (top-right) */}
                  <Card className="border-2 border-dashed border-border hover:border-orange-500/50 transition-colors" data-demo="card-pdf">
                    <CardContent className="p-5">
                      <div
                        className={`flex flex-col items-center justify-center text-center space-y-2 transition-all ${effectiveDragPdf ? 'scale-[1.02]' : ''}`}
                        onDragOver={handlePdfDragOver}
                        onDragLeave={handlePdfDragLeave}
                        onDrop={handlePdfDrop}
                      >
                        <div className={`p-2.5 rounded-full ${effectiveDragPdf ? 'bg-orange-500/20' : 'bg-orange-500/10'}`}>
                          <FileType2 className={`h-6 w-6 ${effectiveDragPdf ? 'text-orange-500' : 'text-orange-400'}`} />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">
                            {effectiveDragPdf ? 'Drop PDFs now' : 'PDF Documents'}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Text extraction via DeepSeek OCR
                          </p>
                        </div>
                        <label className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 border border-border/50 cursor-pointer hover:bg-muted transition-colors" data-demo="pdf-table-toggle">
                          <input
                            type="checkbox"
                            checked={pdfExtractTables}
                            onChange={(e) => setPdfExtractTables(e.target.checked)}
                            className="accent-orange-500 h-3.5 w-3.5"
                          />
                          <Table2 className="h-3.5 w-3.5 text-orange-400" />
                          <span className="text-xs font-medium">Extract tables as separate files</span>
                        </label>
                        <Button size="sm" onClick={() => pdfInputRef.current?.click()} variant="outline" className="mt-1 border-orange-500/30 text-orange-600 dark:text-orange-400 hover:bg-orange-500/10">
                          <Upload className="h-3.5 w-3.5 mr-1.5" />
                          Choose PDFs
                        </Button>
                        <input
                          ref={pdfInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handlePdfInput}
                          accept=".pdf"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Images (bottom-left) */}
                  <Card className="border-2 border-dashed border-border hover:border-teal-500/50 transition-colors" data-demo="card-image">
                    <CardContent className="p-5">
                      <div
                        className={`flex flex-col items-center justify-center text-center space-y-2 transition-all ${effectiveDragImage ? 'scale-[1.02]' : ''}`}
                        onDragOver={handleImageDragOver}
                        onDragLeave={handleImageDragLeave}
                        onDrop={handleImageDrop}
                      >
                        <div className={`p-2.5 rounded-full ${effectiveDragImage ? 'bg-teal-500/20' : 'bg-teal-500/10'}`}>
                          <Image className={`h-6 w-6 ${effectiveDragImage ? 'text-teal-500' : 'text-teal-400'}`} />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">
                            {effectiveDragImage ? 'Drop images now' : 'Images'}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            OCR extraction with mode selection
                          </p>
                        </div>
                        <Button size="sm" onClick={() => imageInputRef.current?.click()} variant="outline" className="mt-1 border-teal-500/30 text-teal-600 dark:text-teal-400 hover:bg-teal-500/10">
                          <Upload className="h-3.5 w-3.5 mr-1.5" />
                          Choose Images
                        </Button>
                        <p className="text-[11px] text-muted-foreground">JPG, PNG, TIFF, BMP, WebP</p>
                        <input
                          ref={imageInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileInput}
                          accept=".jpg,.jpeg,.png,.tiff,.tif,.bmp,.webp"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Audio & Video (bottom-right) */}
                  <Card className="border-2 border-dashed border-border hover:border-purple-500/50 transition-colors" data-demo="card-media">
                    <CardContent className="p-5">
                      <div
                        className={`flex flex-col items-center justify-center text-center space-y-2 transition-all ${effectiveDragMedia ? 'scale-[1.02]' : ''}`}
                        onDragOver={handleMediaDragOver}
                        onDragLeave={handleMediaDragLeave}
                        onDrop={handleMediaDrop}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-full ${effectiveDragMedia ? 'bg-purple-500/20' : 'bg-purple-500/10'}`}>
                            <Music className={`h-5 w-5 ${effectiveDragMedia ? 'text-purple-500' : 'text-purple-400'}`} />
                          </div>
                          <div className={`p-2 rounded-full ${effectiveDragMedia ? 'bg-purple-500/20' : 'bg-purple-500/10'}`}>
                            <Video className={`h-5 w-5 ${effectiveDragMedia ? 'text-purple-500' : 'text-purple-400'}`} />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">
                            {effectiveDragMedia ? 'Drop media now' : 'Audio & Video'}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Transcribed with speaker labels
                          </p>
                        </div>
                        <Button size="sm" onClick={() => mediaInputRef.current?.click()} variant="outline" className="mt-1 border-purple-500/30 text-purple-600 dark:text-purple-400 hover:bg-purple-500/10">
                          <Upload className="h-3.5 w-3.5 mr-1.5" />
                          Choose Media
                        </Button>
                        <p className="text-[11px] text-muted-foreground">MP3, M4A, WAV, MP4, MKV, AVI</p>
                        <input
                          ref={mediaInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileInput}
                          accept=".m4a,.mp3,.wav,.ogg,.flac,.wma,.aac,.webm,.mp4,.mkv,.avi,.mov,.wmv"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* URL Download Tab */}
              <TabsContent value="url" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Download from URL</CardTitle>
                    <CardDescription>
                      Enter a direct link to download files from external sources
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-1 relative">
                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="https://example.com/file.csv or direct download link"
                          value={urlInput}
                          onChange={(e) => setUrlInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !isDownloading) {
                              handleUrlDownload();
                            }
                          }}
                          disabled={isDownloading}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                      <Button
                        onClick={handleUrlDownload}
                        disabled={isDownloading || !urlInput.trim()}
                      >
                        {isDownloading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </>
                        )}
                      </Button>
                    </div>

                    <div className="bg-muted/30 p-4 rounded-lg border border-border/50 text-sm text-muted-foreground">
                      <p className="flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        Supported sources: Direct links, Dropbox, Google Drive (public), and more.
                      </p>
                    </div>

                    {/* Download started indicator — full progress shown in Queue */}
                    {downloadProgress && (
                      <div className="mt-4 p-3 border rounded-lg bg-emerald-500/5 border-emerald-500/20 flex items-center gap-2">
                        <Wifi className="h-4 w-4 text-emerald-500 animate-pulse" />
                        <span className="text-sm text-emerald-700 dark:text-emerald-400">
                          Downloading — see Queue for progress
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Cloud Storage Tab */}
              <TabsContent value="cloud" className="space-y-4" data-demo="cloud-content">
                {/* Org-Level Data Sources */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Data Sources</CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddSource(!showAddSource)}
                      >
                        {showAddSource ? 'Cancel' : '+ Add Source'}
                      </Button>
                    </div>
                    <CardDescription>
                      Cloud storage buckets shared across your organization
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {showAddSource && (
                      <div className="border rounded-lg p-4 bg-muted/30">
                        <DataSourceForm
                          defaultType="s3"
                          typeFilter={['s3', 'gcp']}
                          onCreated={() => {
                            setShowAddSource(false);
                            loadDataSources();
                          }}
                          onCancel={() => setShowAddSource(false)}
                        />
                      </div>
                    )}

                    {dataSources.filter((s) => s.type === 's3' || s.type === 'gcp').length > 0 ? (
                      <div className="space-y-2">
                        {dataSources
                          .filter((s) => s.type === 's3' || s.type === 'gcp')
                          .map((source) => (
                            <div
                              key={source.source_id}
                              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedSourceId === source.source_id
                                  ? 'border-primary bg-primary/5'
                                  : 'hover:bg-muted/50'
                              }`}
                              onClick={() =>
                                setSelectedSourceId(
                                  selectedSourceId === source.source_id ? null : source.source_id
                                )
                              }
                            >
                              <Cloud className={`h-5 w-5 shrink-0 ${source.type === 's3' ? 'text-orange-500' : 'text-blue-500'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{source.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {source.type === 's3' ? 'AWS S3' : 'GCP'} &middot; {source.config?.bucket_name}
                                  {source.status === 'error' && (
                                    <span className="text-destructive ml-1">(connection error)</span>
                                  )}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSource(source.source_id);
                                }}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                      </div>
                    ) : !showAddSource ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No cloud sources configured yet
                      </p>
                    ) : null}

                    {/* Migration banner */}
                    {(hasS3Credentials || hasGcpCredentials) && dataSources.filter((s) => s.type === 's3' || s.type === 'gcp').length === 0 && (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <Info className="h-4 w-4 text-amber-600 shrink-0" />
                        <p className="text-xs text-amber-800 dark:text-amber-200 flex-1">
                          You have legacy credentials. Migrate them to org-level sources for team access.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 text-xs"
                          onClick={handleMigrate}
                          disabled={migrating}
                        >
                          {migrating ? 'Migrating...' : 'Migrate'}
                        </Button>
                      </div>
                    )}

                    {/* File browser for selected source */}
                    {selectedSourceId && (
                      <S3FileBrowser
                        onTransferComplete={handleCloudTransferComplete}
                        provider={dataSources.find((s) => s.source_id === selectedSourceId)?.type === 'gcp' ? 'gcp' : 'aws'}
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Legacy per-user credentials (collapsed) */}
                {!selectedSourceId && dataSources.filter((s) => s.type === 's3' || s.type === 'gcp').length === 0 && (
                  <>
                    <div className="flex items-center gap-2 p-1 bg-muted rounded-lg w-fit">
                      <button
                        onClick={() => setCloudProvider('aws')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          cloudProvider === 'aws'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        AWS S3
                      </button>
                      <button
                        onClick={() => setCloudProvider('gcp')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          cloudProvider === 'gcp'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        GCP Cloud Storage
                      </button>
                    </div>

                    {cloudProvider === 'aws' ? (
                      <>
                        <S3CredentialsForm onCredentialsSaved={handleS3CredentialsSaved} provider="aws" />
                        {hasS3Credentials && (
                          <S3FileBrowser onTransferComplete={handleCloudTransferComplete} provider="aws" />
                        )}
                      </>
                    ) : (
                      <>
                        <S3CredentialsForm onCredentialsSaved={handleGcpCredentialsSaved} provider="gcp" />
                        {hasGcpCredentials && (
                          <S3FileBrowser onTransferComplete={handleCloudTransferComplete} provider="gcp" />
                        )}
                      </>
                    )}
                  </>
                )}
              </TabsContent>

              {/* SQL Ingestion Tab */}
              <TabsContent value="sql" className="space-y-4" data-demo="sql-content">
                <SQLIngestionTab />
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Right Column (hidden for SQL tab) */}
          {effectiveTab !== 'sql' && (
          <div className="space-y-6">

            {/* Upload Progress Card - Only show for direct/url uploads */}
            {effectiveTab !== 'cloud' && (
              <Card className="h-full flex flex-col" data-demo="upload-queue">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Queue</CardTitle>
                    {(hasCompletedFiles || (downloadProgress && downloadProgress.status === 'completed')) && !demoCtx?.active && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          clearCompleted();
                          if (downloadProgress?.status === 'completed') setDownloadProgress(null);
                        }}
                        className="h-8 text-xs"
                      >
                        Clear Completed
                      </Button>
                    )}
                  </div>
                  <CardDescription>
                    {displayQueue.length === 0 && !downloadProgress
                      ? 'No active uploads'
                      : `${displayQueue.length + (downloadProgress ? 1 : 0)} item(s) in queue`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto max-h-[500px] flex flex-col">
                  {displayQueue.length === 0 && !downloadProgress ? (
                    <div className="flex-1 flex flex-col items-center justify-center min-h-[300px] text-muted-foreground border-2 border-dashed rounded-lg border-muted-foreground/20">
                      <File className="h-10 w-10 mb-3 opacity-20" />
                      <p className="text-sm font-medium">Uploads will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3 pr-2">

                      {/* URL Download / Processing Progress */}
                      {downloadProgress && (() => {
                        const isDownloadPhase = downloadProgress.status === 'downloading' || downloadProgress.status === 'starting';
                        const isProcessing = downloadProgress.status === 'processing';
                        const isReady = downloadProgress.status === 'ready';
                        const isFailed = downloadProgress.status === 'failed';
                        const isActive = isDownloadPhase || isProcessing;

                        // Colors shift from emerald (download) → blue (processing) → green (ready)
                        const accentColor = isProcessing ? 'blue' : isReady ? 'green' : isFailed ? 'red' : 'emerald';
                        const borderCls = `border-${accentColor}-500/30`;
                        const bgCls = isProcessing
                          ? 'bg-gradient-to-r from-blue-500/5 via-indigo-500/5 to-violet-500/5 dark:from-blue-500/10 dark:via-indigo-500/10 dark:to-violet-500/10'
                          : isReady
                          ? 'bg-gradient-to-r from-green-500/5 to-emerald-500/5 dark:from-green-500/10 dark:to-emerald-500/10'
                          : 'bg-gradient-to-r from-emerald-500/5 via-teal-500/5 to-cyan-500/5 dark:from-emerald-500/10 dark:via-teal-500/10 dark:to-cyan-500/10';
                        const barGradient = isProcessing
                          ? 'from-blue-500 via-indigo-400 to-violet-500'
                          : 'from-emerald-500 via-teal-400 to-cyan-500';

                        return (
                        <div className={`relative overflow-hidden rounded-lg border ${borderCls} ${bgCls}`}>
                          {/* Animated top accent bar */}
                          <div className={`h-0.5 bg-gradient-to-r ${barGradient}`} style={{
                            backgroundSize: '200% 100%',
                            animation: isActive ? 'shimmer 2s linear infinite' : 'none',
                          }} />

                          <div className="p-3">
                            {/* Phase steps indicator */}
                            <div className="flex items-center gap-1 mb-2.5">
                              {[
                                { label: 'Download', done: !isDownloadPhase },
                                { label: 'Processing', done: isReady },
                                { label: 'Ready', done: isReady },
                              ].map((step, idx) => (
                                <React.Fragment key={step.label}>
                                  {idx > 0 && <div className={`flex-1 h-px ${step.done ? 'bg-green-500' : isActive ? 'bg-muted-foreground/20' : 'bg-muted-foreground/10'}`} />}
                                  <div className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                    step.done ? 'text-green-700 dark:text-green-400 bg-green-500/10' :
                                    (isDownloadPhase && step.label === 'Download') || (isProcessing && step.label === 'Processing')
                                      ? `text-${accentColor}-700 dark:text-${accentColor}-300 bg-${accentColor}-500/15`
                                      : 'text-muted-foreground/40'
                                  }`}>{step.label}</div>
                                </React.Fragment>
                              ))}
                            </div>

                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-3 min-w-0">
                                <div className={`relative p-2 rounded-md border ${
                                  isProcessing ? 'bg-blue-500/10 dark:bg-blue-500/20 border-blue-500/20'
                                  : isReady ? 'bg-green-500/10 dark:bg-green-500/20 border-green-500/20'
                                  : 'bg-emerald-500/10 dark:bg-emerald-500/20 border-emerald-500/20'
                                }`}>
                                  {isProcessing ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                                  ) : isReady ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : isFailed ? (
                                    <AlertCircle className="h-4 w-4 text-destructive" />
                                  ) : (
                                    <Globe className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                  )}
                                  {isDownloadPhase && (
                                    <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-sm truncate max-w-[150px]">
                                    {downloadProgress.filename}
                                  </p>
                                  <p className={`text-[10px] uppercase tracking-wider font-medium ${
                                    isProcessing ? 'text-blue-600/70 dark:text-blue-400/70'
                                    : isReady ? 'text-green-600/70 dark:text-green-400/70'
                                    : 'text-emerald-600/70 dark:text-emerald-400/70'
                                  }`}>
                                    {isDownloadPhase ? 'Downloading' : isProcessing ? 'Processing' : isReady ? 'Ready' : isFailed ? 'Failed' : 'URL Download'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5">
                                {isReady && <CheckCircle className="h-4 w-4 text-green-500" />}
                                {isFailed && <AlertCircle className="h-4 w-4 text-destructive" />}
                                {isDownloadPhase && (
                                  <span className="text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                                    {downloadProgress.progress.toFixed(0)}%
                                  </span>
                                )}
                                {isProcessing && downloadProgress.processingProgress !== undefined && (
                                  <span className="text-xs font-semibold tabular-nums text-blue-700 dark:text-blue-300">
                                    {downloadProgress.processingProgress.toFixed(0)}%
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Download progress bar */}
                            {isDownloadPhase && (
                              <div className="relative h-1.5 bg-emerald-500/10 dark:bg-emerald-500/20 rounded-full overflow-hidden mb-2">
                                <div
                                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-700 ease-out"
                                  style={{ width: `${Math.max(2, downloadProgress.progress)}%` }}
                                />
                                <div
                                  className="absolute inset-y-0 w-8 rounded-full bg-white/30 blur-sm transition-all duration-700 ease-out"
                                  style={{ left: `calc(${Math.max(2, downloadProgress.progress)}% - 16px)` }}
                                />
                              </div>
                            )}

                            {/* Processing progress bar */}
                            {isProcessing && (
                              <div className="relative h-1.5 bg-blue-500/10 dark:bg-blue-500/20 rounded-full overflow-hidden mb-2">
                                <div
                                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-blue-500 to-indigo-400 transition-all duration-700 ease-out"
                                  style={{ width: `${Math.max(2, downloadProgress.processingProgress || 0)}%` }}
                                />
                              </div>
                            )}

                            {/* Download stats */}
                            {downloadProgress.status === 'downloading' && (downloadProgress.speed_mbps || downloadProgress.eta_seconds) && (
                              <div className="flex items-center gap-3 text-[11px] text-emerald-700/70 dark:text-emerald-300/60">
                                {downloadProgress.speed_mbps !== undefined && downloadProgress.speed_mbps > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Wifi className="h-3 w-3" />
                                    {downloadProgress.speed_mbps.toFixed(1)} MB/s
                                  </span>
                                )}
                                {downloadProgress.eta_seconds !== undefined && downloadProgress.eta_seconds > 0 && (
                                  <span>
                                    {downloadProgress.eta_seconds < 60
                                      ? `~${downloadProgress.eta_seconds}s remaining`
                                      : `~${Math.ceil(downloadProgress.eta_seconds / 60)}m remaining`}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Processing stats */}
                            {isProcessing && (
                              <div className="flex items-center gap-3 text-[11px] text-blue-700/70 dark:text-blue-300/60">
                                {downloadProgress.currentChunks !== undefined && downloadProgress.expectedChunks !== undefined && (
                                  <span>{downloadProgress.currentChunks} / {downloadProgress.expectedChunks} chunks</span>
                                )}
                                {downloadProgress.totalRows !== undefined && downloadProgress.totalRows > 0 && (
                                  <span>{downloadProgress.totalRows.toLocaleString()} rows</span>
                                )}
                                {!downloadProgress.currentChunks && !downloadProgress.totalRows && (
                                  <span>Extracting and indexing...</span>
                                )}
                              </div>
                            )}

                            {downloadProgress.status === 'starting' && (
                              <p className="text-[11px] text-emerald-600/60 dark:text-emerald-400/50">Connecting to remote server...</p>
                            )}
                          </div>
                        </div>
                        );
                      })()}

                      {/* Regular file upload queue items */}
                      {displayQueue.map((queueFile, index) => (
                        <div key={index} className="group relative bg-muted/30 border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-3 min-w-0">
                              <div className="p-2 bg-background rounded-md border shadow-sm">
                                <FileText className="h-4 w-4 text-primary" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate max-w-[150px]">
                                  {queueFile.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(queueFile.size)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center">
                              {queueFile.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                              {queueFile.status === 'processing' && <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />}
                              {queueFile.status === 'error' && <AlertCircle className="h-4 w-4 text-destructive" />}
                              {queueFile.status === 'uploading' && (
                                <span className="text-xs font-medium text-primary">{Math.round(queueFile.progress)}%</span>
                              )}
                            </div>
                          </div>

                          {queueFile.status === 'uploading' && (
                            <Progress value={queueFile.progress} className="h-1.5" />
                          )}

                          {queueFile.message && (
                            <p className={`text-xs mt-2 ${queueFile.status === 'error' ? 'text-destructive' :
                                queueFile.status === 'completed' ? 'text-green-600' :
                                  'text-muted-foreground'
                              }`}>
                              {queueFile.message}
                            </p>
                          )}

                          {/* Warnings & Errors */}
                          {(queueFile.warnings?.length || 0) > 0 && (
                            <div className="mt-2 p-2 bg-yellow-500/10 rounded text-xs text-yellow-600 dark:text-yellow-400">
                              <p className="font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Warnings:</p>
                              <ul className="list-disc list-inside mt-1">
                                {queueFile.warnings?.map((w, i) => <li key={i}>{w}</li>)}
                              </ul>
                            </div>
                          )}
                          {(queueFile.errors?.length || 0) > 0 && (
                            <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                              <p className="font-semibold flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Errors:</p>
                              <ul className="list-disc list-inside mt-1">
                                {queueFile.errors?.map((e, i) => <li key={i}>{e}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tips Card */}
            <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/20 dark:to-blue-950/20 border-indigo-100 dark:border-indigo-900/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-indigo-900 dark:text-indigo-100">
                  <Info className="h-4 w-4" />
                  Pro Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2 text-indigo-800 dark:text-indigo-200">
                <p>• Large files? Use Cloud Storage transfer for better reliability.</p>
                <p>• Zipped files (.zip, .gz) are automatically detected.</p>
                <p>• Excel files are auto-converted for fast querying.</p>
                <p>• Audio and video files are automatically transcribed with speaker labels.</p>
              </CardContent>
            </Card>
          </div>
          )}
        </div>
      </div>
      </main>

      {/* Image extraction options modal */}
      {showImageOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleImageUploadCancel}>
          <div className="bg-background border rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Image Extraction Mode</h3>
            <p className="text-sm text-muted-foreground mb-4 break-words">
              {pendingImageFiles.length === 1
                ? pendingImageFiles[0].name
                : `${pendingImageFiles.length} images selected`}
            </p>
            <div className="space-y-2 mb-6">
              {([
                { value: 'document' as const, label: 'Document OCR', desc: 'Extract text as structured markdown with headings, lists, and tables' },
                { value: 'table' as const, label: 'Table Extraction', desc: 'Parse tabular data into spreadsheet columns (invoices, reports)' },
                { value: 'figure' as const, label: 'Figure / Chart Parsing', desc: 'Extract data and structure from charts, diagrams, and graphs' },
                { value: 'describe' as const, label: 'Image Description', desc: 'Generate a detailed natural language description of the image' },
                { value: 'all' as const, label: 'All Methods', desc: 'Run all extraction modes and save results into a single folder' },
              ]).map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    extractMode === opt.value
                      ? 'border-teal-500 bg-teal-500/5'
                      : 'border-border hover:border-teal-500/30 hover:bg-muted/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="extractMode"
                    value={opt.value}
                    checked={extractMode === opt.value}
                    onChange={() => setExtractMode(opt.value)}
                    className="shrink-0 accent-teal-500"
                  />
                  <div>
                    <span className="text-sm font-medium">{opt.label}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleImageUploadCancel}>
                Cancel
              </Button>
              <Button onClick={handleImageUploadConfirm} className="bg-teal-600 hover:bg-teal-700 text-white">
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};